import React, { useState, useEffect } from 'react';
import { ehrSettingsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Database, Settings, CheckCircle, XCircle, ExternalLink, Shield,
  RefreshCw, ChevronRight, Wifi, WifiOff, Pill, Building2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function EHRSettingsPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [configDialog, setConfigDialog] = useState(false);
  const [configForm, setConfigForm] = useState({
    client_id: '', client_secret: '', base_url: '', environment: 'sandbox', enabled: false, notes: ''
  });
  const [testing, setTesting] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await ehrSettingsApi.getAll();
      setProviders(res.data.providers || []);
    } catch (err) {
      console.error('Failed to load EHR settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const openConfig = (provider) => {
    setSelectedProvider(provider);
    setConfigForm({
      client_id: '', client_secret: '', base_url: provider.base_url || '',
      environment: provider.environment || 'sandbox', enabled: provider.enabled,
      notes: provider.notes || ''
    });
    setConfigDialog(true);
  };

  const saveConfig = async () => {
    if (!selectedProvider) return;
    try {
      await ehrSettingsApi.update(selectedProvider.provider_key, {
        provider: selectedProvider.provider_key, ...configForm
      });
      toast.success(`${selectedProvider.name} settings saved`);
      setConfigDialog(false);
      loadSettings();
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const testConnection = async (providerKey) => {
    setTesting(true);
    try {
      const res = await ehrSettingsApi.testConnection(providerKey);
      if (res.data.success) {
        toast.success(`${providerKey}: Connected (${res.data.latency_ms}ms)`);
      } else {
        toast.error(`${providerKey}: ${res.data.message}`);
      }
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const ehrProviders = providers.filter(p => p.provider_key !== 'surescripts');
  const pharmacyProvider = providers.find(p => p.provider_key === 'surescripts');

  if (loading) return <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-8" data-testid="ehr-settings-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          EHR & Pharmacy Integration Settings
        </h1>
        <p className="text-[#64748B] mt-1">
          Configure connections to EHR systems and pharmacy networks
        </p>
      </div>

      <Tabs defaultValue="ehr">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ehr" data-testid="ehr-tab">
            <Database className="w-4 h-4 mr-2" /> EHR Systems
          </TabsTrigger>
          <TabsTrigger value="pharmacy" data-testid="pharmacy-tab">
            <Pill className="w-4 h-4 mr-2" /> Pharmacy Network
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ehr" className="mt-6 space-y-4">
          {ehrProviders.map(provider => (
            <Card key={provider.provider_key} className="careiq-card hover:shadow-md transition-shadow" data-testid={`ehr-provider-${provider.provider_key}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${provider.enabled ? 'bg-[#166534]/10' : 'bg-[#F1F5F9]'}`}>
                      <Building2 className={`w-6 h-6 ${provider.enabled ? 'text-[#166534]' : 'text-[#94A3B8]'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-[#0A2342]">{provider.name}</h3>
                        {provider.enabled ? (
                          <span className="px-2 py-0.5 text-xs bg-[#166534]/10 text-[#166534] rounded-full flex items-center gap-1">
                            <Wifi className="w-3 h-3" /> Connected
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-[#F1F5F9] text-[#94A3B8] rounded-full flex items-center gap-1">
                            <WifiOff className="w-3 h-3" /> Not Connected
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#64748B] mt-1">{provider.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {provider.supported_standards?.map((s, i) => (
                          <span key={i} className="px-2 py-0.5 text-[10px] bg-[#0E7490]/10 text-[#0E7490] rounded">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {provider.enabled && (
                      <Button variant="outline" size="sm" onClick={() => testConnection(provider.provider_key)} disabled={testing} data-testid={`test-${provider.provider_key}`}>
                        <RefreshCw className={`w-3 h-3 mr-1 ${testing ? 'animate-spin' : ''}`} /> Test
                      </Button>
                    )}
                    <Button size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={() => openConfig(provider)} data-testid={`config-${provider.provider_key}`}>
                      <Settings className="w-3 h-3 mr-1" /> Configure
                    </Button>
                  </div>
                </div>

                {/* Quick links */}
                <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex flex-wrap gap-3">
                  <a href={provider.developer_portal} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Developer Portal
                  </a>
                  <a href={provider.api_docs} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> API Docs
                  </a>
                  <a href={provider.setup_guide} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Setup Guide
                  </a>
                </div>

                {/* Registration Steps (collapsible) */}
                <details className="mt-3">
                  <summary className="text-xs text-[#64748B] cursor-pointer hover:text-[#0A2342]">
                    View registration steps ({provider.registration_steps?.length || 0} steps)
                  </summary>
                  <div className="mt-2 ml-4 space-y-1">
                    {provider.registration_steps?.map((step, i) => (
                      <p key={i} className="text-xs text-[#64748B]">{step}</p>
                    ))}
                  </div>
                </details>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="pharmacy" className="mt-6 space-y-6">
          {pharmacyProvider && (
            <>
              <Card className="careiq-card border-l-4 border-l-[#0E7490]" data-testid="pharmacy-surescripts">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${pharmacyProvider.enabled ? 'bg-[#166534]/10' : 'bg-[#0E7490]/10'}`}>
                        <Pill className={`w-7 h-7 ${pharmacyProvider.enabled ? 'text-[#166534]' : 'text-[#0E7490]'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-semibold text-[#0A2342]">{pharmacyProvider.name}</h3>
                          {pharmacyProvider.enabled ? (
                            <span className="px-2 py-0.5 text-xs bg-[#166534]/10 text-[#166534] rounded-full">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-[#F1F5F9] text-[#94A3B8] rounded-full">Not Connected</span>
                          )}
                        </div>
                        <p className="text-sm text-[#64748B] mt-1">{pharmacyProvider.description}</p>
                      </div>
                    </div>
                    <Button size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={() => openConfig(pharmacyProvider)} data-testid="config-surescripts">
                      <Settings className="w-3 h-3 mr-1" /> Configure
                    </Button>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex flex-wrap gap-3">
                    <a href={pharmacyProvider.developer_portal} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Developer Portal
                    </a>
                    <a href={pharmacyProvider.api_docs} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> E-Prescribing Info
                    </a>
                    <a href={pharmacyProvider.setup_guide} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Get Started
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* How Pharmacy Connection Works */}
              <Card className="careiq-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#0E7490]" />
                    How Pharmacy Connection Works
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#64748B]">
                    {pharmacyProvider.pharmacy_connection_info?.how_it_works}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-[#0A2342] mb-2">Routing Process</h4>
                      <p className="text-xs text-[#64748B]">{pharmacyProvider.pharmacy_connection_info?.routing}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-[#0A2342] mb-2">Supported Standards</h4>
                      <div className="flex flex-wrap gap-1">
                        {pharmacyProvider.supported_standards?.map((s, i) => (
                          <span key={i} className="px-2 py-0.5 text-[10px] bg-[#0E7490]/10 text-[#0E7490] rounded">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Features */}
              <Card className="careiq-card">
                <CardHeader>
                  <CardTitle className="text-lg">Pharmacy Network Features</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {pharmacyProvider.pharmacy_connection_info?.features?.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-[#F8FAFC]">
                        <CheckCircle className="w-4 h-4 text-[#166534] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#0A2342]">{f}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Requirements */}
              <Card className="careiq-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#92400E]" />
                    Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {pharmacyProvider.pharmacy_connection_info?.requirements?.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2">
                        <span className="text-xs font-bold text-[#0E7490] mt-0.5">{i + 1}.</span>
                        <span className="text-sm text-[#0A2342]">{r}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Registration Steps */}
              <Card className="careiq-card">
                <CardHeader>
                  <CardTitle className="text-lg">Setup Steps</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pharmacyProvider.registration_steps?.map((step, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0]">
                        <div className="w-6 h-6 rounded-full bg-[#0E7490] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          {i + 1}
                        </div>
                        <span className="text-sm text-[#0A2342]">{step.replace(/^\d+\.\s*/, '')}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Configuration Dialog */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="sm:max-w-md" aria-describedby="config-desc">
          <DialogHeader>
            <DialogTitle>Configure {selectedProvider?.name}</DialogTitle>
            <p id="config-desc" className="text-sm text-[#64748B]">Enter your API credentials</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Environment</Label>
              <select value={configForm.environment} onChange={(e) => setConfigForm({ ...configForm, environment: e.target.value })} className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm" data-testid="config-environment">
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div>
              <Label>Client ID</Label>
              <Input value={configForm.client_id} onChange={(e) => setConfigForm({ ...configForm, client_id: e.target.value })} placeholder="Enter client ID..." className="mt-1" data-testid="config-client-id" />
            </div>
            <div>
              <Label>Client Secret</Label>
              <Input type="password" value={configForm.client_secret} onChange={(e) => setConfigForm({ ...configForm, client_secret: e.target.value })} placeholder="Enter client secret..." className="mt-1" data-testid="config-client-secret" />
            </div>
            <div>
              <Label>Base URL (optional)</Label>
              <Input value={configForm.base_url} onChange={(e) => setConfigForm({ ...configForm, base_url: e.target.value })} placeholder="https://fhir.epic.com/..." className="mt-1" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={configForm.notes} onChange={(e) => setConfigForm({ ...configForm, notes: e.target.value })} placeholder="Optional notes..." className="mt-1" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={configForm.enabled} onChange={(e) => setConfigForm({ ...configForm, enabled: e.target.checked })} className="rounded border-[#E2E8F0]" data-testid="config-enabled" />
              <Label>Enable this integration</Label>
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={saveConfig} data-testid="save-config-btn">
              Save Configuration
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
